import React from 'react';

import * as JKYModel from '../custom-types';
import {
  ANSWER_LETTERS,
  DIFFICULTY_LABELS,
  TeamFaces,
  GlobalRank,
  TriviaAvatar,
  formatPoints,
  ordinal,
  rankBy,
  roundLabel,
  useCountdown,
} from './TriviaShared';

import './TriviaScreen.scss';

interface TriviaScreenProps {
  trivia: JKYModel.TriviaState;
  venueName: string;
  /** A patron's song from the queue, when one is playing under the questions. */
  songTitle?: string | null;
}

/**
 * Trivia night on the venue's TV. It sits over the music video, which plays blurred behind
 * it, and under the header and the QR code, which stay where patrons expect them.
 *
 * The server runs the game; this only draws whatever phase it is in and counts down to the
 * end time it was sent.
 */
const TriviaScreen = ({ trivia, venueName, songTitle }: TriviaScreenProps) => {
  const phase = trivia.phase;
  const players = trivia.players || [];
  const teams = trivia.mode === 'teams' ? trivia.teams || [] : null;
  const rounds = trivia.rounds || [];
  const round = rounds[trivia.roundIndex || 0];

  return (
    <div className={`trivia-stage trivia-phase-${phase}`}>
      {phase === 'lobby' && (
        <Lobby trivia={trivia} venueName={venueName} players={players} teams={teams} rounds={rounds} />
      )}
      {phase === 'loading' && (
        <div className="trivia-center">
          <div className="trivia-kicker">Round {(trivia.roundIndex || 0) + 1}</div>
          <h1 className="trivia-huge">Getting the questions…</h1>
        </div>
      )}
      {phase === 'roundIntro' && <RoundIntro trivia={trivia} round={round} />}
      {(phase === 'question' || phase === 'reveal') && !!trivia.question && (
        <QuestionView trivia={trivia} round={round} players={players} teams={teams} />
      )}
      {(phase === 'standings' || (phase === 'final' && !!trivia.rolling)) && (
        <Standings trivia={trivia} players={players} teams={teams} />
      )}
      {phase === 'final' &&
        !trivia.rolling &&
        (teams ? (
          <FinalTeams teams={teams} kicker="Final scores" />
        ) : (
          <FinalScores players={players} kicker="Final scores" />
        ))}

      <div className="trivia-footer">
        {songTitle ? (
          <span className="trivia-music">&#9835; {songTitle}</span>
        ) : (
          !!trivia.music &&
          trivia.music.videoIds.length > 0 && <span className="trivia-music">&#9835; {trivia.music.name}</span>
        )}
        {!!trivia.credit && <span className="trivia-credit">{trivia.credit}</span>}
      </div>
    </div>
  );
};

interface LobbyProps {
  trivia: JKYModel.TriviaState;
  venueName: string;
  players: JKYModel.TriviaPlayer[];
  teams: JKYModel.TriviaTeam[] | null;
  rounds: JKYModel.TriviaRoundInfo[];
}

const Lobby = ({ trivia, venueName, players, teams, rounds }: LobbyProps) => (
  <div className="trivia-lobby">
    <div className="trivia-lobby-intro">
      <div className="trivia-kicker">{venueName}</div>
      <h1 className="trivia-title">Trivia Night</h1>
      <p className="trivia-lead">
        {teams ? (
          <>
            Scan the code to play on your phone. Sign in with your Jookable account, then <b>start a team</b> or{' '}
            <b>join</b> one.
          </>
        ) : (
          <>
            Scan the code to play on your phone. Sign in with your Jookable account and tap <b>Join</b>.
          </>
        )}
      </p>
      <ol className="trivia-round-list">
        {rounds.map((r, index) => (
          <li key={index} className="trivia-round-item">
            <span className="trivia-round-number">Round {index + 1}</span>
            <span className="trivia-round-name">{r.random ? 'Random category' : r.category}</span>
            <span className="trivia-round-meta">
              {DIFFICULTY_LABELS[r.difficulty]} · {r.questionCount} questions
            </span>
          </li>
        ))}
      </ol>
      {!!trivia.error && <div className="trivia-error">{trivia.error}</div>}
    </div>

    {teams ? (
      <LobbyTeams teams={teams} />
    ) : (
      <div className="trivia-lobby-players">
        <div className="trivia-label">
          {players.length === 0
            ? 'Waiting for players'
            : `${players.length} ${players.length === 1 ? 'player' : 'players'} in`}
        </div>
        <ul className="trivia-joined">
          {players.slice(0, 24).map((p) => (
            <li key={p.id} className="trivia-joined-person">
              <TriviaAvatar person={p} className="trivia-avatar-lobby" />
              <span>{p.username}</span>
              <GlobalRank person={p} />
            </li>
          ))}
        </ul>
        {players.length > 24 && <div className="trivia-more">and {players.length - 24} more</div>}
        <div className="trivia-waiting">The game starts when the host is ready.</div>
      </div>
    )}
  </div>
);

const LobbyTeams = ({ teams }: { teams: JKYModel.TriviaTeam[] }) => {
  const people = teams.reduce((sum, t) => sum + t.members.length, 0);
  return (
    <div className="trivia-lobby-players">
      <div className="trivia-label">
        {teams.length === 0
          ? 'Waiting for teams'
          : `${teams.length} ${teams.length === 1 ? 'team' : 'teams'} · ${people} ${people === 1 ? 'player' : 'players'}`}
      </div>
      <ul className="trivia-team-list">
        {teams.slice(0, 10).map((team) => (
          <li key={team.id} className="trivia-team-row">
            <span className="trivia-team-name">{team.name}</span>
            <TeamFaces members={team.members} max={6} avatarClassName="trivia-avatar-row" />
          </li>
        ))}
      </ul>
      {teams.length > 10 && <div className="trivia-more">and {teams.length - 10} more teams</div>}
      <div className="trivia-waiting">The game starts when the host is ready.</div>
    </div>
  );
};

const RoundIntro = ({ trivia, round }: { trivia: JKYModel.TriviaState; round: JKYModel.TriviaRoundInfo }) => {
  const { seconds } = useCountdown(trivia, 5);
  return (
    <div className="trivia-center">
      <div className="trivia-kicker">
        {trivia.rolling ? `Round ${trivia.lap || 1}` : `Round ${(trivia.roundIndex || 0) + 1} of ${trivia.roundCount}`}
      </div>
      <h1 className="trivia-huge">{round ? round.category : ''}</h1>
      <div className="trivia-chips">
        {!!round && <span className="trivia-chip">{DIFFICULTY_LABELS[round.difficulty]}</span>}
        <span className="trivia-chip">{trivia.questionCount} questions</span>
      </div>
      {!!trivia.error && <div className="trivia-error">{trivia.error}</div>}
      <div className="trivia-get-ready">Get ready · {seconds}</div>
      {!!trivia.rolling && <div className="trivia-join-anytime">Join anytime. Scan the code to play.</div>}
    </div>
  );
};

interface QuestionViewProps {
  trivia: JKYModel.TriviaState;
  round: JKYModel.TriviaRoundInfo;
  players: JKYModel.TriviaPlayer[];
  teams: JKYModel.TriviaTeam[] | null;
}

const QuestionView = ({ trivia, round, players, teams }: QuestionViewProps) => {
  const revealing = trivia.phase === 'reveal';
  const total = revealing ? trivia.revealSeconds || 10 : trivia.answerSeconds || 20;
  const { seconds, share } = useCountdown(trivia, total);
  const question = trivia.question;
  const answered = players.filter((p) => p.answered);

  return (
    <div className={revealing ? 'trivia-play is-revealing' : 'trivia-play'}>
      <div className="trivia-main">
        <div className="trivia-question-top">
          <span className="trivia-chip">
            {trivia.rolling ? roundLabel(round) : `Round ${(trivia.roundIndex || 0) + 1} · ${roundLabel(round)}`}
          </span>
          <span className="trivia-count">
            Question {(trivia.questionIndex || 0) + 1} of {trivia.questionCount}
          </span>
          <span className={revealing ? 'trivia-timer is-next' : 'trivia-timer'}>
            {revealing ? `Next in ${seconds}` : seconds}
          </span>
        </div>

        <div className="trivia-question-card">
          <div className="trivia-question-text">{question.text}</div>
        </div>

        <ul className={`trivia-answers trivia-answers-${question.answers.length}`}>
          {question.answers.map((answer, index) => {
            const isRight = revealing && trivia.correctIndex === index;
            const classes = [
              'trivia-answer',
              `trivia-answer-${index}`,
              revealing ? (isRight ? 'is-right' : 'is-wrong') : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <li key={index} className={classes}>
                <span className="trivia-letter">{ANSWER_LETTERS[index]}</span>
                <span className="trivia-answer-text">{answer}</span>
                {isRight && <span className="trivia-answer-check">&#10003;</span>}
              </li>
            );
          })}
        </ul>

        <div className="trivia-timebar">
          <div className="trivia-timebar-fill" style={{ transform: `scaleX(${share})` }} />
        </div>

        {revealing && <GotItRight players={players} teams={teams} />}

        {!revealing && (
          <div className="trivia-answered">
            <span>
              {trivia.answeredCount} of {trivia.playingCount != null ? trivia.playingCount : players.length} answered
            </span>
            <span className="trivia-answered-faces">
              {answered.slice(0, 16).map((p) => (
                <TriviaAvatar key={p.id} person={p} className="trivia-avatar-small" />
              ))}
            </span>
          </div>
        )}
      </div>

      {revealing && (teams ? <TeamLeaderboard teams={teams} /> : <Leaderboard players={players} />)}
    </div>
  );
};

/**
 * Once the answer shows: everyone who got it right, by name, so a table can cheer them.
 * In team play, each team with somebody right and how many of them were.
 */
const GotItRight = ({ players, teams }: { players: JKYModel.TriviaPlayer[]; teams: JKYModel.TriviaTeam[] | null }) => {
  if (teams) {
    const right = teams.filter((team) => team.lastRightCount > 0);
    return (
      <div className="trivia-got-it">
        <div className="trivia-label">{right.length === 0 ? 'Nobody got that one' : 'Got it right'}</div>
        <ul className="trivia-got-it-list">
          {right.slice(0, 8).map((team) => (
            <li key={team.id} className="trivia-got-it-person">
              <span>{team.name}</span>
              <span className="trivia-got-it-count">
                {team.lastRightCount} of {team.members.length}
              </span>
            </li>
          ))}
        </ul>
        {right.length > 8 && <div className="trivia-more">and {right.length - 8} more teams</div>}
      </div>
    );
  }
  const right = players.filter((p) => p.lastCorrect);
  return (
    <div className="trivia-got-it">
      <div className="trivia-label">{right.length === 0 ? 'Nobody got that one' : 'Got it right'}</div>
      <ul className="trivia-got-it-list">
        {right.slice(0, 10).map((p) => (
          <li key={p.id} className="trivia-got-it-person">
            <TriviaAvatar person={p} className="trivia-avatar-small" />
            <span>{p.username}</span>
            <span className="trivia-plus">+{formatPoints(p.lastPoints)}</span>
          </li>
        ))}
      </ul>
      {right.length > 10 && <div className="trivia-more">and {right.length - 10} more</div>}
    </div>
  );
};

interface StandingsProps {
  trivia: JKYModel.TriviaState;
  players: JKYModel.TriviaPlayer[];
  teams: JKYModel.TriviaTeam[] | null;
}

/**
 * Between rounds: the all-time board across every jukebox, the game so far, then the round
 * just played, left to right in that order.
 */
const Standings = ({ trivia, players, teams }: StandingsProps) => {
  const rolling = !!trivia.rolling;
  const { seconds } = useCountdown(trivia, rolling ? 20 : 15);
  // A rolling game's rounds are its laps: one round each, numbered through the day.
  const roundNumber = rolling ? trivia.lap || 1 : (trivia.roundIndex || 0) + 1;
  const next = rolling ? null : (trivia.rounds || [])[roundNumber];
  const here = new Set(players.map((p) => p.id));
  const leaders = trivia.globalLeaders || [];
  // In a rolling game the round's points are its score; the player rankings are the day's.
  const overall = (x: { score: number; sessionScore?: number }) => (rolling ? x.sessionScore || 0 : x.score);
  const ofRound = (x: { score: number; roundScore?: number }) => (rolling ? x.score : x.roundScore || 0);

  return (
    <div className="trivia-standings">
      <div className="trivia-standings-top">
        <div className="trivia-kicker">Round {roundNumber} done</div>
        <div className="trivia-standings-next">
          {next
            ? `Round ${roundNumber + 1} · ${roundLabel(next)} in ${seconds}`
            : `Next round in ${seconds}${rolling ? ' · Scan the code to join' : ''}`}
        </div>
      </div>
      <div className="trivia-standings-columns">
        <section className="trivia-board trivia-standings-col">
          <div className="trivia-label">Global rankings</div>
          {leaders.length === 0 ? (
            <p className="trivia-board-empty">No wins yet. Win a round to get on the board.</p>
          ) : (
            <ol className="trivia-board-list">
              {leaders.map((leader) => (
                <li key={leader.id} className={here.has(leader.id) ? 'trivia-board-row is-here' : 'trivia-board-row'}>
                  <span className="trivia-board-rank">{leader.globalRank}</span>
                  <TriviaAvatar person={leader} className="trivia-avatar-row" />
                  <span className="trivia-board-name">{leader.username}</span>
                  <span className="trivia-board-score">
                    {leader.wins} {leader.wins === 1 ? 'win' : 'wins'}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="trivia-board trivia-standings-col">
          <div className="trivia-label">{teams ? 'Team rankings' : 'Player rankings'}</div>
          {teams ? (
            <TeamRows ranked={rankBy<JKYModel.TriviaTeam>(teams, overall)} score={overall} />
          ) : (
            <PlayerRows ranked={rankBy<JKYModel.TriviaPlayer>(players, overall)} score={overall} />
          )}
        </section>

        <section className="trivia-board trivia-standings-col">
          <div className="trivia-label">Round {roundNumber} rankings</div>
          {teams ? (
            <TeamRows ranked={rankBy<JKYModel.TriviaTeam>(teams, ofRound)} score={ofRound} />
          ) : (
            <PlayerRows ranked={rankBy<JKYModel.TriviaPlayer>(players, ofRound)} score={ofRound} />
          )}
        </section>
      </div>
    </div>
  );
};

interface PlayerRowsProps {
  ranked: { item: JKYModel.TriviaPlayer; rank: number }[];
  score(p: JKYModel.TriviaPlayer): number;
}

const PlayerRows = ({ ranked, score }: PlayerRowsProps) =>
  ranked.length === 0 ? (
    <p className="trivia-board-empty">Nobody has joined yet. Scan the code to play.</p>
  ) : (
    <ol className="trivia-board-list">
      {ranked.slice(0, 6).map(({ item: p, rank }) => (
        <li key={p.id} className="trivia-board-row">
          <span className="trivia-board-rank">{rank}</span>
          <TriviaAvatar person={p} className="trivia-avatar-row" />
          <span className="trivia-board-name">{p.username}</span>
          <span className="trivia-board-score">{formatPoints(score(p))}</span>
        </li>
      ))}
    </ol>
  );

interface TeamRowsProps {
  ranked: { item: JKYModel.TriviaTeam; rank: number }[];
  score(t: JKYModel.TriviaTeam): number;
}

const TeamRows = ({ ranked, score }: TeamRowsProps) =>
  ranked.length === 0 ? (
    <p className="trivia-board-empty">No teams yet. Scan the code to start one.</p>
  ) : (
    <ol className="trivia-board-list">
      {ranked.slice(0, 6).map(({ item: team, rank }) => (
        <li key={team.id} className="trivia-board-row">
          <span className="trivia-board-rank">{rank}</span>
          <span className="trivia-board-name">{team.name}</span>
          <TeamFaces members={team.members} max={3} avatarClassName="trivia-avatar-small" />
          <span className="trivia-board-score">{formatPoints(score(team))}</span>
        </li>
      ))}
    </ol>
  );

const Leaderboard = ({ players }: { players: JKYModel.TriviaPlayer[] }) => {
  if (players.length === 0) {
    return (
      <aside className="trivia-board">
        <div className="trivia-label">Leaderboard</div>
        <p className="trivia-board-empty">Nobody has joined yet. Scan the code to play.</p>
      </aside>
    );
  }
  const leader = players[0];
  const rightCount = players.filter((p) => p.lastCorrect).length;
  return (
    <aside className="trivia-board">
      <div className="trivia-label">{leader.score > 0 ? 'In the lead' : 'Leaderboard'}</div>
      <div className="trivia-leader">
        <TriviaAvatar person={leader} className="trivia-avatar-leader" />
        <div className="trivia-leader-text">
          <div className="trivia-leader-name">
            {leader.username} {leader.lastCorrect && <span className="trivia-right-mark">&#10003;</span>}
          </div>
          <GlobalRank person={leader} showWins className="trivia-global-rank-leader" />
          <div className="trivia-leader-score">{formatPoints(leader.score)} pts</div>
        </div>
      </div>
      <ol className="trivia-board-list">
        {players.slice(1, 5).map((p) => (
          <li key={p.id} className={p.lastCorrect ? 'trivia-board-row is-right' : 'trivia-board-row'}>
            <span className="trivia-board-rank">{p.rank}</span>
            <TriviaAvatar person={p} className="trivia-avatar-row" />
            <span className="trivia-board-name">
              {p.username} <GlobalRank person={p} />
            </span>
            {p.lastCorrect && <span className="trivia-right-mark">&#10003;</span>}
            {p.lastPoints > 0 && <span className="trivia-plus">+{formatPoints(p.lastPoints)}</span>}
            <span className="trivia-board-score">{formatPoints(p.score)}</span>
          </li>
        ))}
      </ol>
      <div className="trivia-board-note">
        {rightCount === 0 ? 'Nobody got that one.' : `${rightCount} of ${players.length} got it right.`}
      </div>
    </aside>
  );
};

const TeamLeaderboard = ({ teams }: { teams: JKYModel.TriviaTeam[] }) => {
  if (teams.length === 0) {
    return (
      <aside className="trivia-board">
        <div className="trivia-label">Leaderboard</div>
        <p className="trivia-board-empty">No teams yet. Scan the code to start one.</p>
      </aside>
    );
  }
  const leader = teams[0];
  return (
    <aside className="trivia-board">
      <div className="trivia-label">{leader.score > 0 ? 'Team in the lead' : 'Leaderboard'}</div>
      <div className="trivia-leader trivia-leader-team">
        <div className="trivia-leader-name">{leader.name}</div>
        <TeamFaces
          members={leader.members}
          max={5}
          className="trivia-faces-leader"
          avatarClassName="trivia-avatar-team-leader"
        />
        <div className="trivia-leader-score">
          {formatPoints(leader.score)} pts
          {leader.lastPoints > 0 && <span className="trivia-plus"> +{formatPoints(leader.lastPoints)}</span>}
        </div>
      </div>
      <ol className="trivia-board-list">
        {teams.slice(1, 5).map((team) => (
          <li key={team.id} className={team.lastRightCount > 0 ? 'trivia-board-row is-right' : 'trivia-board-row'}>
            <span className="trivia-board-rank">{team.rank}</span>
            <span className="trivia-board-name">{team.name}</span>
            <TeamFaces members={team.members} max={3} avatarClassName="trivia-avatar-small" />
            {team.lastPoints > 0 && <span className="trivia-plus">+{formatPoints(team.lastPoints)}</span>}
            <span className="trivia-board-score">{formatPoints(team.score)}</span>
          </li>
        ))}
      </ol>
      <div className="trivia-board-note">A team scores the average of its players' points.</div>
    </aside>
  );
};

// Rolling trivia ends a game every few minutes, so each one is numbered.

const FinalTeams = ({ teams, kicker }: { teams: JKYModel.TriviaTeam[]; kicker: string }) => {
  const podium = [teams[1], teams[0], teams[2]];
  const rest = teams.slice(3, 9);
  return (
    <div className="trivia-final">
      <div className="trivia-kicker">{kicker}</div>
      <h1 className="trivia-title">{teams[0] ? `${teams[0].name} win!` : 'Thanks for playing'}</h1>
      <div className="trivia-podium">
        {podium.map((team, slot) =>
          team ? (
            <div key={team.id} className={`trivia-podium-place trivia-podium-${PODIUM_SLOTS[slot]}`}>
              <TeamFaces
                members={team.members}
                max={4}
                className="trivia-faces-podium"
                avatarClassName="trivia-avatar-team-podium"
              />
              <div className="trivia-podium-name">{team.name}</div>
              <div className="trivia-podium-score">{formatPoints(team.score)}</div>
              <div className="trivia-podium-step">{ordinal(team.rank)}</div>
            </div>
          ) : (
            <div key={`empty-${slot}`} className="trivia-podium-place is-empty" />
          ),
        )}
      </div>
      {rest.length > 0 && (
        <ol className="trivia-final-rest">
          {rest.map((team) => (
            <li key={team.id}>
              <span className="trivia-board-rank">{team.rank}</span>
              <span className="trivia-board-name">{team.name}</span>
              <TeamFaces members={team.members} max={3} avatarClassName="trivia-avatar-small" />
              <span className="trivia-board-score">{formatPoints(team.score)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

// Second on the left, the winner in the middle, third on the right.
const PODIUM_SLOTS = ['second', 'first', 'third'];

const FinalScores = ({ players, kicker }: { players: JKYModel.TriviaPlayer[]; kicker: string }) => {
  const podium = [players[1], players[0], players[2]];
  const rest = players.slice(3, 10);
  return (
    <div className="trivia-final">
      <div className="trivia-kicker">{kicker}</div>
      <h1 className="trivia-title">{players[0] ? `${players[0].username} wins!` : 'Thanks for playing'}</h1>
      <div className="trivia-podium">
        {podium.map((p, slot) =>
          p ? (
            <div key={p.id} className={`trivia-podium-place trivia-podium-${PODIUM_SLOTS[slot]}`}>
              <TriviaAvatar person={p} className="trivia-avatar-podium" />
              <div className="trivia-podium-name">{p.username}</div>
              <GlobalRank person={p} showWins className="trivia-global-rank-podium" />
              <div className="trivia-podium-score">{formatPoints(p.score)}</div>
              <div className="trivia-podium-step">{ordinal(p.rank)}</div>
            </div>
          ) : (
            <div key={`empty-${slot}`} className="trivia-podium-place is-empty" />
          ),
        )}
      </div>
      {rest.length > 0 && (
        <ol className="trivia-final-rest">
          {rest.map((p) => (
            <li key={p.id}>
              <span className="trivia-board-rank">{p.rank}</span>
              <TriviaAvatar person={p} className="trivia-avatar-row" />
              <span className="trivia-board-name">
                {p.username} <GlobalRank person={p} />
              </span>
              <span className="trivia-board-score">{formatPoints(p.score)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default TriviaScreen;
